import React, { useState } from "react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "./ui/dialog";

const SIZE_CHART_CM = [
  { size: "XS-Tall", bust: "80–84", waist: "60–64", hip: "86–90", height: "175–180" },
  { size: "S-Tall",  bust: "84–88", waist: "64–68", hip: "90–94", height: "178–183" },
  { size: "M-Tall",  bust: "88–92", waist: "68–72", hip: "94–98", height: "180–185" },
  { size: "L-Tall",  bust: "92–98", waist: "72–78", hip: "98–104", height: "180–185" },
  { size: "XL-Tall", bust: "98–104", waist: "78–84", hip: "104–110", height: "180–185" },
];

const SIZE_CHART_IN = [
  { size: "XS-Tall", bust: "31½–33", waist: "23½–25¼", hip: "34–35½", height: "5'9\"–5'11\"" },
  { size: "S-Tall",  bust: "33–34½", waist: "25¼–26¾", hip: "35½–37", height: "5'10\"–6'0\"" },
  { size: "M-Tall",  bust: "34½–36¼", waist: "26¾–28¼", hip: "37–38½", height: "5'11\"–6'1\"" },
  { size: "L-Tall",  bust: "36¼–38½", waist: "28¼–30¾", hip: "38½–41", height: "5'11\"–6'1\"" },
  { size: "XL-Tall", bust: "38½–41", waist: "30¾–33", hip: "41–43¼", height: "5'11\"–6'1\"" },
];

const INSEAM_INFO = [
  { label: "Standard tall inseam", cm: "81 cm", inch: "32\"" },
  { label: "Extended tall inseam", cm: "86 cm", inch: "34\"" },
];

export const SizeGuideModal = ({ trigger }) => {
  const [unit, setUnit] = useState("cm");
  const data = unit === "cm" ? SIZE_CHART_CM : SIZE_CHART_IN;

  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className="max-w-2xl bg-[#f0e9e1] text-[#1a1814] border border-[#1a1814]/15 rounded-none p-0"
        data-testid="size-guide-modal"
      >
        <div className="px-6 sm:px-10 pt-10 pb-8">
          <div className="micro-label text-[#1a1814]/60 mb-2">Drafted for height</div>
          <DialogTitle className="font-display text-3xl sm:text-4xl text-[#1a1814] leading-tight font-normal tracking-normal">
            Size guide — Tall fit
          </DialogTitle>
          <DialogDescription className="text-sm text-[#1a1814]/65 italic mt-3 leading-relaxed max-w-md">
            Every TIA piece is cut for women 5'9" and above. Body lengths run 4–5cm longer, sleeves 3cm longer, and we offer two inseam lengths on bottoms.
          </DialogDescription>

          <div className="mt-7 inline-flex border border-[#1a1814]/20" role="tablist" aria-label="Units">
            <button
              type="button"
              onClick={() => setUnit("cm")}
              className={`px-5 py-2 micro-label ${unit === "cm" ? "bg-[#1a1814] text-[#f0e9e1]" : "text-[#1a1814]/70 hover:text-[#1a1814]"}`}
              data-testid="size-guide-toggle-cm"
              role="tab"
              aria-selected={unit === "cm"}
            >
              Centimetres
            </button>
            <button
              type="button"
              onClick={() => setUnit("in")}
              className={`px-5 py-2 micro-label ${unit === "in" ? "bg-[#1a1814] text-[#f0e9e1]" : "text-[#1a1814]/70 hover:text-[#1a1814]"}`}
              data-testid="size-guide-toggle-in"
              role="tab"
              aria-selected={unit === "in"}
            >
              Inches
            </button>
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full text-sm" data-testid="size-guide-table">
              <thead>
                <tr className="border-y border-[#1a1814]/25">
                  <th className="text-left py-3 pr-4 micro-label text-[#1a1814]/60 font-normal">Size</th>
                  <th className="text-left py-3 pr-4 micro-label text-[#1a1814]/60 font-normal">Bust</th>
                  <th className="text-left py-3 pr-4 micro-label text-[#1a1814]/60 font-normal">Waist</th>
                  <th className="text-left py-3 pr-4 micro-label text-[#1a1814]/60 font-normal">Hip</th>
                  <th className="text-left py-3 pr-2 micro-label text-[#1a1814]/60 font-normal">Height</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.size} className="border-b border-[#1a1814]/10">
                    <td className="py-3 pr-4 font-serif text-base">{row.size}</td>
                    <td className="py-3 pr-4 text-[#1a1814]/80">{row.bust}</td>
                    <td className="py-3 pr-4 text-[#1a1814]/80">{row.waist}</td>
                    <td className="py-3 pr-4 text-[#1a1814]/80">{row.hip}</td>
                    <td className="py-3 pr-2 text-[#1a1814]/80">{row.height}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8 border-t border-[#1a1814]/10 pt-6">
            <div className="micro-label text-[#1a1814]/60 mb-3">Inseam options on bottoms</div>
            <div className="grid grid-cols-2 gap-4">
              {INSEAM_INFO.map((i) => (
                <div key={i.label} className="border border-[#1a1814]/15 p-4">
                  <div className="text-xs text-[#1a1814]/60 mb-1">{i.label}</div>
                  <div className="font-serif text-xl">{unit === "cm" ? i.cm : i.inch}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-8 border-t border-[#1a1814]/10 pt-6">
            <div className="micro-label text-[#1a1814]/60 mb-3">How to measure</div>
            <ul className="text-sm text-[#1a1814]/75 space-y-2 leading-relaxed">
              <li><span className="text-[#1a1814]">Bust —</span> measure around the fullest part, tape parallel to the floor.</li>
              <li><span className="text-[#1a1814]">Waist —</span> measure the narrowest part of your natural waist.</li>
              <li><span className="text-[#1a1814]">Hip —</span> measure around the fullest part of your hips, feet together.</li>
              <li><span className="text-[#1a1814]">Inseam —</span> measure from the crotch seam to the ankle on a pair of trousers that fit your length.</li>
            </ul>
            <p className="text-xs italic text-[#1a1814]/55 mt-5">
              Between sizes? We recommend sizing up for our oversized pieces, true to size for sculpted styles. Reply to your order confirmation if you'd like a personal fitting note.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
